import { useCallback, useEffect, useRef, useState } from "react";

interface EventLog {
  id: string;
  eventType: string;
  session: string;
  messageId: string | null;
  validationResult: { accepted: boolean; reason?: string } | null;
  processingResult: Record<string, unknown> | null;
  error: string | null;
  processingTimeMs: number | null;
  createdAt: string;
}

interface NivelFonte {
  kind: "sse" | "polling";
  label: string;
}

const POLLING_FALLBACK: NivelFonte = { kind: "polling", label: "Atualização automática (10s)" };
const SSE_ATIVO: NivelFonte = { kind: "sse", label: "Tempo real (SSE)" };

export function EventsPage() {
  const [events, setEvents] = useState<EventLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [source, setSource] = useState<NivelFonte>(POLLING_FALLBACK);
  const sourceRef = useRef<EventSource | null>(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem("affiliate_flow_auth_token");
      const response = await fetch("/api/v1/events?pageSize=50", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.data.items || []);
      } else {
        setError(data.error || "Erro ao carregar eventos.");
      }
    } catch {
      setError("Erro de conexão.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregamento inicial (histórico) + fallback de polling.
  useEffect(() => {
    load();
    let fallback: number | null = null;

    const startFallback = () => {
      setSource(POLLING_FALLBACK);
      if (fallback === null && autoRefresh) {
        fallback = window.setInterval(load, 10000);
      }
    };

    if (autoRefresh && typeof EventSource !== "undefined") {
      try {
        const es = new EventSource("/api/v1/events/stream", { withCredentials: true });
        sourceRef.current = es;

        let current: EventLog[] = [];
        load().then(() => {
          // Snapshot para acumular eventos incrementais.
          setEvents((prev) => {
            current = prev;
            return prev;
          });
        });

        es.onmessage = (message) => {
          try {
            const parsed = JSON.parse(message.data) as { type: string; data?: EventLog };
            if (parsed.type === "connected") {
              setSource(SSE_ATIVO);
              setError(null);
            } else if (parsed.type === "event" && parsed.data?.id) {
              current = [parsed.data, ...current].slice(0, 50);
              setEvents(current);
            }
          } catch {
            // Payload inválido: ignora
          }
        };

        // Falha no stream (auth indisponível, proxy, rede): cai para polling.
        es.onerror = () => {
          es.close();
          sourceRef.current = null;
          startFallback();
        };
      } catch {
        startFallback();
      }
    } else {
      startFallback();
    }

    return () => {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      if (fallback !== null) clearInterval(fallback);
    };
  }, [load, autoRefresh]);

  const eventColor: Record<string, string> = {
    "message.received": "bg-blue-500/10 text-blue-700",
    "message.ignored": "bg-yellow-500/10 text-yellow-700",
    "offer.extracted": "bg-green-500/10 text-green-700",
    "offer.duplicate": "bg-orange-500/10 text-orange-700",
    "publication.sent": "bg-green-500/10 text-green-700",
    "publication.failed": "bg-red-500/10 text-red-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {events.length} eventos registrados
        </h3>
        <div className="flex items-center gap-3 text-sm">
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              source.kind === "sse" ? "bg-green-500" : "bg-yellow-500"
            }`}
            aria-hidden
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            {source.label}
          </label>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {loading
        ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        )
        : events.length === 0
        ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Nenhum evento registrado ainda.
          </div>
        )
        : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Data/Hora</th>
                  <th className="px-3 py-2 font-medium">Evento</th>
                  <th className="px-3 py-2 font-medium">Mensagem</th>
                  <th className="px-3 py-2 font-medium">Resultado</th>
                  <th className="px-3 py-2 font-medium">Tempo</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {new Date(event.createdAt).toLocaleString("pt-BR")}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          eventColor[event.eventType] || "bg-muted"
                        }`}
                      >
                        {event.eventType}
                      </span>
                    </td>
                    <td className="max-w-32 truncate px-3 py-2 font-mono text-xs">
                      {event.messageId || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {event.validationResult
                        ? event.validationResult.accepted
                          ? "Aceito"
                          : `Ignorado (${event.validationResult.reason || "—"})`
                        : event.error
                        ? `Erro: ${event.error.slice(0, 30)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {event.processingTimeMs ? `${event.processingTimeMs}ms` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
